import React, { useState, useRef, useEffect, useCallback } from 'react';

interface CameraCaptureProps {
  onCapture: (imageBase64: string) => void;
  onClose: () => void;
  facingMode?: 'user' | 'environment';
  title?: string;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({
  onCapture,
  onClose,
  facingMode = 'environment',
  title = 'Take Photo'
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [cameraReady, setCameraReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);

  const startCamera = useCallback(async () => {
    try {
      setError(null);

      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
          setCameraReady(true);
        };
      }
    } catch (err: any) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera permissions and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else if (err.name === 'NotReadableError') {
        setError('Camera is in use by another application.');
      } else {
        setError('Unable to access camera. Please try again.');
      }
    }
  }, [facingMode]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
  }, []);

  useEffect(() => {
    startCamera();

    return () => {
      stopCamera();
    };
  }, [startCamera, stopCamera]);

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current || !cameraReady) return;

    setCapturing(true);

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert to base64 (JPEG with 90% quality)
      const imageBase64 = canvas.toDataURL('image/jpeg', 0.9).split(',')[1];

      // Stop camera before returning
      stopCamera();

      // Return the image
      onCapture(imageBase64);
    }

    setCapturing(false);
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  const handleRetry = () => {
    setError(null);
    startCamera();
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        <div style={styles.header}>
          <button onClick={handleClose} style={styles.closeButton}>
            ← Back
          </button>
          <span style={styles.title}>{title}</span>
          <div style={{ width: '60px' }} /> {/* Spacer for centering */}
        </div>

        <div style={styles.cameraArea}>
          {error ? (
            <div style={styles.errorContainer}>
              <div style={styles.errorIcon}>📷</div>
              <p style={styles.errorText}>{error}</p>
              <button onClick={handleRetry} style={styles.retryButton}>
                Try Again
              </button>
              <button onClick={handleClose} style={styles.cancelButton}>
                Cancel
              </button>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  ...styles.video,
                  opacity: cameraReady ? 1 : 0
                }}
              />
              {!cameraReady && (
                <div style={styles.loading}>
                  <div style={styles.spinner}></div>
                  <p>Starting camera...</p>
                </div>
              )}
              <canvas ref={canvasRef} style={styles.canvas} />
            </>
          )}
        </div>

        {!error && (
          <div style={styles.controls}>
            <div style={styles.captureButtonContainer}>
              <button
                onClick={handleCapture}
                disabled={!cameraReady || capturing}
                style={{
                  ...styles.captureButton,
                  opacity: cameraReady && !capturing ? 1 : 0.5
                }}
              >
                <div style={styles.captureButtonInner} />
              </button>
            </div>
            <p style={styles.hint}>
              Position the receipt within the frame and tap to capture
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#000',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#fff',
    fontSize: '16px',
    padding: '8px 12px',
    cursor: 'pointer',
    minWidth: '60px',
    textAlign: 'left',
  },
  title: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: '600',
  },
  cameraArea: {
    flex: 1,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  canvas: {
    display: 'none',
  },
  loading: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    gap: '16px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    textAlign: 'center',
    color: '#fff',
    gap: '16px',
  },
  errorIcon: {
    fontSize: '48px',
    opacity: 0.5,
  },
  errorText: {
    fontSize: '16px',
    lineHeight: '1.5',
    maxWidth: '300px',
    margin: 0,
  },
  retryButton: {
    backgroundColor: '#059669',
    color: '#fff',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '48px',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: '#fff',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
    minHeight: '48px',
  },
  controls: {
    padding: '24px 16px',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  captureButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  captureButton: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    border: '4px solid rgba(255, 255, 255, 0.3)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.1s',
  },
  captureButtonInner: {
    width: '58px',
    height: '58px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    border: '2px solid #000',
  },
  hint: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '14px',
    margin: 0,
    textAlign: 'center',
  },
};

// Add keyframes for spinner animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
if (!document.querySelector('style[data-camera-capture]')) {
  styleSheet.setAttribute('data-camera-capture', 'true');
  document.head.appendChild(styleSheet);
}

export default CameraCapture;
